import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './LandingPage.module.css';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const { t } = useTranslation('landing');

  const FAQS = [
    { q: t('faq.q0'), a: t('faq.a0') },
    {
      q: t('faq.q1'),
      a: (
        <>
          {t('faq.a1_intro')}<br />
          <b>{t('faq.a1_organizer_label')}</b> {t('faq.a1_organizer_desc')}<br />
          <b>{t('faq.a1_buyer_label')}</b> {t('faq.a1_buyer_desc')}<br />
          {t('faq.a1_note')}<br />
          {t('faq.a1_donation')}<br />
          {t('faq.a1_disclaimer')}
        </>
      ),
    },
    { q: t('faq.q2'), a: t('faq.a2') },
    { q: t('faq.q3'), a: t('faq.a3') },
    { q: t('faq.q4'), a: t('faq.a4') },
  ];

  const FEATURES = [
    {
      icon: <img src="/icons/ticket.svg" alt={t('features.ticket.title')} style={{width: '1.2em', height: '1.2em'}} />,
      title: t('features.ticket.title'),
      desc: t('features.ticket.desc'),
    },
    { icon: '📋', title: t('features.listing.title'), desc: t('features.listing.desc') },
    { icon: '📸', title: t('features.gallery.title'), desc: t('features.gallery.desc') },
    { icon: '💝', title: t('features.donation.title'), desc: t('features.donation.desc') },
    { icon: '📊', title: t('features.dashboard.title'), desc: t('features.dashboard.desc') },
    {
      icon: <img src="/icons/key.svg" alt={t('features.payment.title')} style={{width: '1.2em', height: '1.2em'}} />,
      title: t('features.payment.title'),
      desc: t('features.payment.desc'),
    },
  ];

  const STEPS = [
    { step: '01', title: t('steps.step1.title'), desc: t('steps.step1.desc') },
    { step: '02', title: t('steps.step2.title'), desc: t('steps.step2.desc') },
    { step: '03', title: t('steps.step3.title'), desc: t('steps.step3.desc') },
    { step: '04', title: t('steps.step4.title'), desc: t('steps.step4.desc') },
  ];

  return (
    <div className={styles.page}>
      <Helmet>
        <title>IRIE TIX — チケット販売を始めよう | Start Selling Tickets</title>
        <meta name="description" content="IRIE TIXでチケット販売を開始しましょう。Start selling tickets for your Tokyo events on IRIE TIX." />
        <meta property="og:title" content="IRIE TIX — Start Selling Tickets" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* ─── HERO ─── */}
      <section className={styles.hero}>
        <div className={styles.heroBg} aria-hidden="true" />
        <div className={styles.heroOverlay} aria-hidden="true" />
        <div className={styles.heroInner}>
          <p className={styles.heroEyebrow}>{t('hero.eyebrow')}</p>
          <h1 className={styles.heroTitle}>
            {t('hero.title').split('\n').map((line, i) => <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>)}
          </h1>
          <p className={styles.heroSub}>
            {t('hero.sub').split('\n').map((line, i) => <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>)}
          </p>
          <div className={styles.heroActions}>
            <Link className={styles.ctaPrimary} to="/seller-register">{t('hero.ctaStart')}</Link>
            <a className={styles.ctaSecondary} href="#features">{t('hero.ctaFeatures')}</a>
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section id="features" className={styles.section}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>{t('features.sectionTitle')}</h2>
          <p className={styles.sectionSub}>{t('features.sectionSub')}</p>
          <div className={styles.featuresGrid}>
            {FEATURES.map((f) => (
              <article key={f.title} className={styles.featureCard}>
                <div className={styles.featureIcon}>{f.icon}</div>
                <h3 className={styles.featureTitle}>{f.title}</h3>
                <p className={styles.featureDesc}>{f.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ─── DONATION APPEAL ─── */}
      <section className={`${styles.section} ${styles.donationSection}`}>
        <div className={styles.container}>
          <div className={styles.appealRow}>
            <div className={styles.appealText}>
              <span className={styles.appealBadge}>NEW</span>
              <h2 className={styles.appealTitle}>{t('donationSection.title')}</h2>
              <p className={styles.appealDesc}>
                {t('donationSection.desc')}
              </p>
              <ul className={styles.appealList}>
                <li>{t('donationSection.point1')}</li>
                <li>{t('donationSection.point2')}</li>
                <li>{t('donationSection.point3')}</li>
              </ul>
            </div>
            <div className={styles.appealVisual} aria-hidden="true">
              <div className={styles.donationCard}>
                <div className={styles.donationHeart}>💝</div>
                <div className={styles.donationLabel}>{t('donationSection.donationLabel')}</div>
                <div className={styles.donationAmounts}>
                  {['¥500', '¥1,000', '¥2,000', '¥5,000'].map((a) => (
                    <span key={a} className={styles.donationAmount}>{a}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── GALLERY APPEAL ─── */}
      <section className={`${styles.section} ${styles.gallerySection}`}>
        <div className={styles.container}>
          <div className={`${styles.appealRow} ${styles.appealRowReverse}`}>
            <div className={styles.appealText}>
              <h2 className={styles.appealTitle}>{t('gallerySection.title')}</h2>
              <p className={styles.appealDesc}>
                {t('gallerySection.desc')}
              </p>
              <ul className={styles.appealList}>
                <li>{t('gallerySection.point1')}</li>
                <li>{t('gallerySection.point2')}</li>
                <li>{t('gallerySection.point3')}</li>
              </ul>
            </div>
            <div className={styles.appealVisual} aria-hidden="true">
              <div className={styles.galleryMock}>
                <div className={styles.galleryGrid}>
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className={styles.galleryCell} style={{ opacity: 0.4 + i * 0.1 }} />
                  ))}
                </div>
                <div className={styles.galleryLabel}>{t('gallerySection.albumLabel')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section className={`${styles.section} ${styles.pricingSection}`}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>{t('pricing.sectionTitle')}</h2>
          <p className={styles.sectionSub}>{t('pricing.sectionSub')}</p>
          <div className={styles.pricingGrid}>
            <div className={styles.pricingCard}>
              <div className={styles.pricingIcon}>
                <img src="/icons/ticket.svg" alt={t('pricing.ticketAlt')} style={{width: '1.2em', height: '1.2em'}} />
              </div>
              <div className={styles.pricingName}>{t('pricing.ticketName')}</div>
              <div className={styles.pricingRate}>{t('pricing.ticketRate')}</div>
              <div className={styles.pricingNote}>{t('pricing.ticketNote')}</div>
            </div>
            <div className={styles.pricingCard}>
              <div className={styles.pricingIcon}>💝</div>
              <div className={styles.pricingName}>{t('pricing.donationName')}</div>
              <div className={styles.pricingRate}>15<span className={styles.pricingUnit}>%</span></div>
              <div className={styles.pricingNote}>{t('pricing.donationNote')}</div>
            </div>
            <div className={`${styles.pricingCard} ${styles.pricingFree}`}>
              <div className={styles.pricingIcon}>📸</div>
              <div className={styles.pricingName}>{t('pricing.galleryName')}</div>
              <div className={styles.pricingRate}>{t('pricing.galleryRate')}</div>
              <div className={styles.pricingNote}>{t('pricing.galleryNote')}</div>
            </div>
          </div>
          <p className={styles.pricingDisclaimer}>{t('pricing.pricingDisclaimer')}</p>
        </div>
      </section>
      <section className={`${styles.section} ${styles.stepsSection}`}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>{t('steps.sectionTitle')}</h2>
          <div className={styles.steps}>
            {STEPS.map((s, i) => (
              <div key={s.step} className={styles.stepRow}>
                <div className={styles.stepNum}>{s.step}</div>
                {i < STEPS.length - 1 && <div className={styles.stepLine} aria-hidden="true" />}
                <div className={styles.stepContent}>
                  <div className={styles.stepTitle}>{s.title}</div>
                  <div className={styles.stepDesc}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className={`${styles.section} ${styles.faqSection}`}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>{t('faq.sectionTitle')}</h2>
          <div className={styles.faqList}>
            {FAQS.map((faq, i) => (
              <div key={i} className={styles.faqItem}>
                <button
                  className={styles.faqQuestion}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span>Q. {faq.q}</span>
                  <span className={`${styles.faqChevron} ${openFaq === i ? styles.faqChevronOpen : ''}`}>▼</span>
                </button>
                {openFaq === i && (
                  <div className={styles.faqAnswer}>A. {faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─── */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <h2 className={styles.ctaTitle}>{t('cta.title')}</h2>
          <p className={styles.ctaSub}>{t('cta.sub')}</p>
          <Link className={styles.ctaPrimary} to="/seller-register">{t('cta.button')}</Link>
        </div>
      </section>

    </div>
  );
}
